/**
 * PatientCheckInPage - Public page for patient check-in via QR code
 * 
 * Handles:
 * - Code validation
 * - Auth state (redirect to login if needed)
 * - Loading check-in form template
 * - Pre-populating with patient data
 * - CMR snapshot creation on submission
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Chip,
  TextField,
} from '@mui/material';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import {
  CheckCircle,
  Error as ErrorIcon,
  Login,
  PersonAdd,
  LocalHospital,
  Schedule,
  EventBusy,
} from '@mui/icons-material';
import { AuthContext } from '@healthdesk/shared-hooks';
import {
  validateCheckInCode,
  getCheckInTemplate,
  getDefaultCheckInTemplate,
  createCheckInVisit,
  isClinicOpenForCheckIn,
  getFormattedOperatingHours,
  updatePatientCMR,
  getPatientCMRWithHistory,
  getFeeForType,
  initiateFeePayment,
  getPaymentStatus,
  updateInviteStatus,
} from '@healthdesk/shared-services';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useClinicContext } from '../../contexts/ClinicContext';
import PatientCheckInForm from './PatientCheckInForm';
import CheckInConversation from './CheckInConversation';
import CheckInMedicationsReview from './CheckInMedicationsReview';
import CheckInReviewAndCorrect from './CheckInReviewAndCorrect';
import PatientIDCaptureStep from './PatientIDCaptureStep';
import CheckInFaceStep from './CheckInFaceStep';
import { getTemplateForTouchpoint } from '@healthdesk/shared-services';
import { llmProviderService } from '../../services/llm/llmProviderService';
import { getFirebaseHttpsCallableUserMessage } from '../../utils/firebaseHttpsCallableUserMessage';

// Validation states
const ValidationState = {
  LOADING: 'loading',
  VALID: 'valid',
  INVALID: 'invalid',
  CLOSED: 'closed', // Clinic is closed for check-ins
  ERROR: 'error',
};

// Face verification: required on first check-in (enrollment) and on subsequent (verify); staff override only on failure
const FACE_VERIFICATION_ENABLED = true;

/**
 * MULTI-FACILITY TODO: When PETs are org-scoped, resolve `isTargeted` from org/facility policy
 * in addition to the clinic template document before tagging visits.
 */
function withCheckInMethodForTargetedTemplate(formData, template) {
  if (!template?.isTargeted) return formData;
  return { ...formData, checkInMethod: 'targeted_invite' };
}

const PatientCheckInPage = () => {
  const { clinicId, templateId, code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userData, loading: authLoading } = useContext(AuthContext);
  const { setClinicContext } = useClinicContext();

  // State
  const [validationState, setValidationState] = useState(ValidationState.LOADING);
  const [template, setTemplate] = useState(null);
  const [clinicInfo, setClinicInfo] = useState(null);
  const [error, setError] = useState(null);
  const [checkInComplete, setCheckInComplete] = useState(false);
  const [visitId, setVisitId] = useState(null);
  const [clinicHoursStatus, setClinicHoursStatus] = useState(null); // For closed clinics
  const [operatingHours, setOperatingHours] = useState(null);
  /** null | 'loading' | 'allowed' | 'denied' | 'error' — error = callable/network; denied = policy */
  const [providerApprovalState, setProviderApprovalState] = useState(null);
  const [providerApprovalErrorMessage, setProviderApprovalErrorMessage] = useState(null);
  const [providerApprovalRetryToken, setProviderApprovalRetryToken] = useState(0);

  // CMR history data
  const [cmrHistory, setCmrHistory] = useState({ akaNames: [], previousDOBs: [] });

  // Check-in step state: 'id-capture' | 'form' | 'conversation' | 'medications-review' | 'review-and-correct' | 'payment' | 'submitting'
  const [checkInStep, setCheckInStep] = useState('form');
  const [pendingFormData, setPendingFormData] = useState(null);
  const [resolvedConvoTemplate, setResolvedConvoTemplate] = useState(null);
  const [idCaptureData, setIdCaptureData] = useState(null); // { idScan, insuranceScan }

  // Registration fee (check-in fee) — loaded when clinic is valid
  const [registrationFee, setRegistrationFee] = useState(null);
  const [paymentStepState, setPaymentStepState] = useState({ status: 'idle', paymentId: null, phone: '', error: null });
  const PAYMENT_POLL_INTERVAL_MS = 2500;
  const PAYMENT_POLL_MAX_ATTEMPTS = 60;

  /** One id per check-in attempt on this page — passed to createCheckInVisit for idempotent retries */
  const checkInClientRequestIdRef = useRef(null);
  const ensureCheckInClientRequestId = () => {
    if (!checkInClientRequestIdRef.current) {
      checkInClientRequestIdRef.current =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `ci_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
    }
    return checkInClientRequestIdRef.current;
  };

  // Validate code on mount (code is optional -- invite-link entry skips QR validation)
  useEffect(() => {
    if (clinicId && templateId) {
      validateCode();
    }
  }, [clinicId, templateId, code]);

  const isClinicChartmindProviderRole =
    userData?.role === 'professional-chartmind' || userData?.role === 'pro2';

  // Provider scan: check approval then redirect to ChartMind with clinic context (Phase 3)
  useEffect(() => {
    if (
      validationState !== ValidationState.VALID ||
      authLoading ||
      !user ||
      !userData?.role ||
      !isClinicChartmindProviderRole ||
      !clinicId
    ) {
      return;
    }
    let cancelled = false;
    setProviderApprovalState('loading');
    setProviderApprovalErrorMessage(null);
    const checkApproval = async () => {
      try {
        const functions = getFunctions();
        const checkProviderApprovalFn = httpsCallable(functions, 'checkProviderApproval');
        const { data } = await checkProviderApprovalFn({ clinicId });
        const result = data;
        if (cancelled) return;
        if (result?.allowed) {
          setProviderApprovalState('allowed');
          setClinicContext({
            activeClinicId: clinicId,
            activeClinicCode: code,
            approvalStatus: 'allowed',
          });
          navigate(`/dashboard/chartmind?clinicId=${encodeURIComponent(clinicId)}&code=${encodeURIComponent(code)}`, { replace: true });
        } else {
          setProviderApprovalState('denied');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[PatientCheckIn] Provider approval check failed:', err);
        const { userMessage } = getFirebaseHttpsCallableUserMessage(err);
        setProviderApprovalErrorMessage(userMessage);
        setProviderApprovalState('error');
      }
    };
    checkApproval();
    return () => { cancelled = true; };
  }, [validationState, authLoading, user, userData?.role, isClinicChartmindProviderRole, clinicId, code, setClinicContext, navigate, providerApprovalRetryToken]);

  // Load CMR history when user is authenticated (patient flow)
  useEffect(() => {
    const loadCMRHistory = async () => {
      if (user?.uid && validationState === ValidationState.VALID) {
        try {
          const cmrData = await getPatientCMRWithHistory(user.uid);
          if (cmrData) {
            setCmrHistory({
              akaNames: cmrData.akaNames || [],
              previousDOBs: cmrData.previousDOBs || [],
            });
          }
        } catch (err) {
          console.error('[PatientCheckIn] Error loading CMR history:', err);
        }
      }
    };
    loadCMRHistory();
  }, [user?.uid, validationState]);

  const validateCode = async () => {
    setValidationState(ValidationState.LOADING);
    setError(null);

    try {
      // If a QR code is present, validate it; otherwise the user arrived via invite link
      if (code) {
        const isValid = await validateCheckInCode(clinicId, templateId, code);
        
        if (!isValid) {
          setValidationState(ValidationState.INVALID);
          setError('This check-in code is no longer valid. Please ask the clinic staff for a new QR code.');
          return;
        }
      }

      // Load the specific check-in template identified by the URL
      const templateData = templateId
        ? await getCheckInTemplate(clinicId, templateId)
        : await getDefaultCheckInTemplate(clinicId);
      
      if (!templateData) {
        setValidationState(ValidationState.ERROR);
        setError('This clinic has not configured their check-in form yet. Please check in at the front desk.');
        return;
      }

      // Check if clinic is currently open for check-ins
      const hoursStatus = await isClinicOpenForCheckIn(clinicId);
      setClinicHoursStatus(hoursStatus);
      
      if (!hoursStatus.isOpen) {
        // Load full operating hours for display
        const hours = await getFormattedOperatingHours(clinicId);
        setOperatingHours(hours);
        setClinicInfo({
          id: clinicId,
          name: templateData.clinicName || 'Healthcare Clinic',
        });
        setValidationState(ValidationState.CLOSED);
        return;
      }

      setTemplate(templateData);
      setClinicInfo({
        id: clinicId,
        name: templateData.clinicName || 'Healthcare Clinic',
      });

      // Determine initial check-in step based on check-in template settings
      if (templateData.requirePhotoID || templateData.requireInsuranceCard) {
        setCheckInStep('id-capture');
      } else {
        setCheckInStep('form');
      }

      setValidationState(ValidationState.VALID);

      // Load registration (check-in) fee config for payment step
      try {
        const fee = await getFeeForType(clinicId, 'registration');
        setRegistrationFee(fee && fee.enabled && fee.amountKsh >= 1 ? fee : null);
      } catch (feeErr) {
        console.warn('[PatientCheckIn] Could not load fee config:', feeErr);
        setRegistrationFee(null);
      }
    } catch (err) {
      console.error('[PatientCheckIn] Validation error:', err);
      setValidationState(ValidationState.ERROR);
      setError('Unable to validate check-in code. Please try again.');
    }
  };

  const handleLogin = () => {
    // Redirect to auth with return URL
    const returnUrl = encodeURIComponent(location.pathname);
    navigate(`/auth?returnUrl=${returnUrl}`);
  };

  const handleCreateAccount = () => {
    // Redirect to auth with role and return URL
    const returnUrl = encodeURIComponent(location.pathname);
    navigate(`/auth?role=patient&returnUrl=${returnUrl}`);
  };

  // Handler: ID capture step completes → advance to form
  const handleIDCaptureComplete = (captureResult) => {
    setIdCaptureData(captureResult);
    setCheckInStep('form');
  };

  // Helper: whether the scripted MedicationsConversation step runs after the form (and after
  // CheckInConversation when a conversation template is configured and resolves).
  // Sequence: form → optional LLM conversation → optional medications-review → review-and-correct
  // (when conversation existed) or submit; createCheckInVisit receives pendingFormData at submit.
  //
  // MULTI-FACILITY TODO: medicationsReview is a per-template setting that applies uniformly
  // regardless of delivery channel (in-clinic vs targeted remote). When multi-facility support
  // is added, consider whether different facilities should override this per-clinic for the same template.
  const shouldShowMedsReview = () => {
    return template?.medicationsReview && template.medicationsReview !== 'none';
  };

  const handleCheckInSubmit = async (formData) => {
    try {
      // Check if this template has a conversation to run
      if (template?.conversationTemplateRef && checkInStep === 'form') {
        const resolved = await getTemplateForTouchpoint({
          touchpoint: 'securelink',
          clinicId,
          adminId: template?.encounterTemplateRef?.adminId || template?.conversationTemplateRef?.adminId,
          specialty: Array.isArray(template?.specialties) ? template.specialties[0] : '',
          explicitRef: template.conversationTemplateRef,
        });
        if (resolved) {
          setPendingFormData(formData);
          setResolvedConvoTemplate(resolved);
          setCheckInStep('conversation');
          return; // Don't submit yet; show conversation first
        }
      }

      // No conversation — but check for medications review
      if (checkInStep === 'form' && shouldShowMedsReview()) {
        setPendingFormData(formData);
        setCheckInStep('medications-review');
        return;
      }

      // Face verification: before creating visit, require enrollment (first time) or verification (returning)
      if (FACE_VERIFICATION_ENABLED && !formData.faceVerificationStatus) {
        const hasFaceEnrolled = !!(userData?.faceRecognition?.enrolledAt ?? userData?.faceRecognition?.externalId);
        setPendingFormData(formData);
        setCheckInStep(hasFaceEnrolled ? 'face-verification' : 'face-enrollment');
        return;
      }

      // First, update the master CMR if any fields were modified
      if (formData.modifiedFields && formData.modifiedFields.length > 0) {
        
        // Build updates object from edited values
        const cmrUpdates = {};
        const previousValues = {};
        
        // Map form paths to CMR field names
        const pathToField = {
          'patient.firstName': 'firstName',
          'patient.lastName': 'lastName',
          'patient.dateOfBirth': 'dateOfBirth',
          'patient.sex': 'sex',
          'patient.phone': 'phone',
          'patient.email': 'email',
          'patient.address.street': 'address.street',
          'patient.address.city': 'address.city',
          'patient.address.state': 'address.state',
          'patient.address.zip': 'address.zip',
          'patient.emergencyContact.name': 'emergencyContact.name',
          'patient.emergencyContact.relationship': 'emergencyContact.relationship',
          'patient.emergencyContact.phone': 'emergencyContact.phone',
        };
        
        formData.modifiedFields.forEach(path => {
          const fieldName = pathToField[path];
          if (fieldName && formData.editedValues[path] !== undefined) {
            cmrUpdates[fieldName] = formData.editedValues[path];
          }
          // Track previous values for history
          if (formData.originalValues[path] !== undefined) {
            previousValues[fieldName] = formData.originalValues[path];
          }
        });

        // Determine if name changed for history tracking
        if (formData.nameChanged && formData.previousName) {
          previousValues.firstName = formData.previousName.firstName;
          previousValues.lastName = formData.previousName.lastName;
        }
        
        // Determine if DOB changed for history tracking
        if (formData.dobChanged && formData.previousDOB) {
          previousValues.dateOfBirth = formData.previousDOB;
        }

        // Update the master CMR with history tracking
        await updatePatientCMR(user.uid, cmrUpdates, {
          previousValues,
          trackNameHistory: formData.nameChanged,
          trackDOBHistory: formData.dobChanged,
        });
        
      }

      // Add current history to the form data for the snapshot
      const enhancedFormData = {
        ...formData,
        akaNames: formData.nameChanged && formData.previousName 
          ? [...cmrHistory.akaNames, formData.previousName]
          : cmrHistory.akaNames,
        previousDOBs: formData.dobChanged && formData.previousDOB
          ? [...cmrHistory.previousDOBs, formData.previousDOB]
          : cmrHistory.previousDOBs,
      };

      // Attach ID/insurance scan data if captured
      if (idCaptureData?.idScan) {
        enhancedFormData.idScan = idCaptureData.idScan;
      }
      if (idCaptureData?.insuranceScan) {
        enhancedFormData.insuranceScan = idCaptureData.insuranceScan;
      }

      // Face verification audit (enrolled | verified | staff_override)
      if (formData.faceVerificationStatus) {
        enhancedFormData.faceVerificationStatus = formData.faceVerificationStatus;
        enhancedFormData.faceVerifiedByStaff = formData.faceVerifiedByStaff || null;
      }

      // If clinic requires a registration fee, show payment step first
      if (registrationFee?.enabled && registrationFee?.amountKsh >= 1) {
        setPendingFormData(enhancedFormData);
        setPaymentStepState({
          status: 'idle',
          paymentId: null,
          phone: (userData?.phone || userData?.patient?.phone || '').replace(/\D/g, '').replace(/^0/, '') ? `254${(userData?.phone || userData?.patient?.phone || '').replace(/\D/g, '').replace(/^0/, '')}` : '',
          error: null,
        });
        setCheckInStep('payment');
        return;
      }

      // Create the check-in visit with the snapshot (stable id avoids duplicate visits on double-submit / retry)
      const enhancedWithRequestId = withCheckInMethodForTargetedTemplate(
        {
          ...enhancedFormData,
          checkInClientRequestId: ensureCheckInClientRequestId(),
        },
        template
      );
      const result = await createCheckInVisit(clinicId, user.uid, template.id, enhancedWithRequestId);
      setVisitId(result.visitId);
      updateInviteStatus(clinicId, template.id, user.uid, result.visitId).catch((e) =>
        console.warn('[PatientCheckIn] updateInviteStatus', e)
      );

      // Store active check-in in localStorage for queue view
      const activeCheckIn = {
        visitId: result.visitId,
        clinicId: clinicId,
        clinicName: clinicInfo?.name || 'Healthcare Clinic',
        checkedInAt: new Date().toISOString(),
      };
      localStorage.setItem('activeCheckIn', JSON.stringify(activeCheckIn));
      
      // Trigger storage event for other components (like PatientDrawer)
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'activeCheckIn',
        newValue: JSON.stringify(activeCheckIn),
      }));
      
      // Redirect to queue view instead of showing static success
      navigate('/dashboard/my-queue');
    } catch (err) {
      console.error('[PatientCheckIn] Submit error:', err);
      throw err; // Let the form handle the error display
    }
  };

  const handleConversationComplete = async (conversationResults) => {
    let finalFormData = { ...pendingFormData, conversationResults };

    // Run ROS extraction when intake conversation completed (for ROS CMR card)
    if (conversationResults?.findings?.length || conversationResults?.transcript?.length) {
      try {
        const { output } = await llmProviderService.callLLMWithQuery(
          'securelink-ros-extraction',
          { conversationResults },
          { module: 'PatientCheckInPage', featureName: 'securelink-ros-extraction' }
        );
        const raw = typeof output === 'string' ? output : output?.text ?? output;
        const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
        let rosResults = null;
        try {
          let parsed = null;
          try {
            parsed = JSON.parse(str);
          } catch (_) {
            const match = str.match(/\[[\s\S]*\]/);
            if (match) parsed = JSON.parse(match[0]);
          }
          if (Array.isArray(parsed) && parsed.length > 0 && parsed.some((p) => p && (p.system != null || p.systemName != null))) {
            rosResults = parsed.map((p) => ({
              system: p.system ?? p.systemName ?? '',
              value: (p.value ?? p.summary ?? '').trim() || 'No complaints',
            }));
          }
        } catch (_) {
          // Keep rosResults null if parsing fails
        }
        if (rosResults?.length) {
          finalFormData = { ...finalFormData, rosResults };
        }
      } catch (err) {
        console.warn('[PatientCheckIn] ROS extraction failed:', err);
      }
    }

    setPendingFormData(finalFormData);

    // After conversation, check if medications review is needed
    if (shouldShowMedsReview()) {
      setCheckInStep('medications-review');
      return;
    }

    if (finalFormData.conversationResults) {
      setCheckInStep('review-and-correct');
    } else {
      setCheckInStep('submitting');
      await handleCheckInSubmit(finalFormData);
    }
  };

  const handleConversationSkip = async () => {
    // After skipping conversation, check if medications review is needed
    if (shouldShowMedsReview()) {
      setCheckInStep('medications-review');
      return;
    }

    setCheckInStep('submitting');
    await handleCheckInSubmit(pendingFormData);
  };

  const handleMedicationsReviewComplete = async () => {
    if (pendingFormData.conversationResults) {
      setCheckInStep('review-and-correct');
    } else {
      setCheckInStep('submitting');
      await handleCheckInSubmit(pendingFormData);
    }
  };

  const handleMedicationsReviewSkip = async () => {
    if (pendingFormData.conversationResults) {
      setCheckInStep('review-and-correct');
    } else {
      setCheckInStep('submitting');
      await handleCheckInSubmit(pendingFormData);
    }
  };

  const handleReviewAndCorrectConfirm = (correctedConversationResults) => {
    const finalFormData = { ...pendingFormData, conversationResults: correctedConversationResults };
    setPendingFormData(finalFormData);
    setCheckInStep('submitting');
    handleCheckInSubmit(finalFormData);
  };

  const handleFaceEnrollmentSuccess = () => {
    setCheckInStep('submitting');
    handleCheckInSubmit({ ...pendingFormData, faceVerificationStatus: 'enrolled' });
  };

  const handleFaceVerificationSuccess = () => {
    setCheckInStep('submitting');
    handleCheckInSubmit({ ...pendingFormData, faceVerificationStatus: 'verified' });
  };

  const handlePayRegistrationFee = async () => {
    if (!registrationFee || !clinicId || !pendingFormData) return;
    const provider = (registrationFee.provider || 'paystack').toLowerCase();
    const phone = (paymentStepState.phone || '').trim().replace(/\D/g, '');
    const normalizedPhone = phone.startsWith('254') ? phone : phone ? `254${phone.replace(/^0/, '')}` : '';
    if (provider === 'paystack' && !normalizedPhone) {
      setPaymentStepState(prev => ({ ...prev, error: 'Please enter your M-Pesa phone number (254XXXXXXXXX).' }));
      return;
    }
    setPaymentStepState(prev => ({ ...prev, status: 'initiating', error: null }));
    const referenceId = `checkin-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    try {
      const result = await initiateFeePayment({
        clinicId,
        feeType: 'registration',
        phoneNumber: provider === 'paystack' ? normalizedPhone : undefined,
        referenceType: 'checkin_session',
        referenceId,
        metadata: {},
      });
      setPaymentStepState(prev => ({ ...prev, paymentId: result.paymentId, status: 'pending' }));
      if (provider === 'stripe' && result.clientSecret) {
        setPaymentStepState(prev => ({ ...prev, status: 'idle', error: 'Card payment (Stripe) is not yet available in check-in. Please ask the clinic to use M-Pesa (Paystack) for the registration fee, or complete check-in without fee if the clinic allows.' }));
        return;
      }
      let attempts = 0;
      const poll = async () => {
        if (attempts >= PAYMENT_POLL_MAX_ATTEMPTS) {
          setPaymentStepState(prev => ({ ...prev, status: 'idle', error: 'Payment timed out. Please try again.' }));
          return;
        }
        attempts += 1;
        const statusResult = await getPaymentStatus(result.paymentId);
        if (statusResult.status === 'paid') {
          const formWithFee = withCheckInMethodForTargetedTemplate(
            {
              ...pendingFormData,
              feePaymentId: result.paymentId,
              registrationFeeKsh: registrationFee.amountKsh,
              registrationPaymentStatus: 'paid',
              checkInClientRequestId: ensureCheckInClientRequestId(),
            },
            template
          );
          setCheckInStep('submitting');
          const visitResult = await createCheckInVisit(clinicId, user.uid, template.id, formWithFee);
          setVisitId(visitResult.visitId);
          updateInviteStatus(clinicId, template.id, user.uid, visitResult.visitId).catch((e) =>
            console.warn('[PatientCheckIn] updateInviteStatus', e)
          );
          const activeCheckIn = {
            visitId: visitResult.visitId,
            clinicId,
            clinicName: clinicInfo?.name || 'Healthcare Clinic',
            checkedInAt: new Date().toISOString(),
          };
          localStorage.setItem('activeCheckIn', JSON.stringify(activeCheckIn));
          window.dispatchEvent(new StorageEvent('storage', { key: 'activeCheckIn', newValue: JSON.stringify(activeCheckIn) }));
          navigate('/dashboard/my-queue');
          return;
        }
        if (statusResult.status === 'failed') {
          setPaymentStepState(prev => ({ ...prev, status: 'idle', error: 'Payment failed. Please try again.' }));
          return;
        }
        setTimeout(poll, PAYMENT_POLL_INTERVAL_MS);
      };
      setTimeout(poll, PAYMENT_POLL_INTERVAL_MS);
    } catch (err) {
      console.warn('[PatientCheckIn] Fee payment error:', err);
      const { userMessage } = getFirebaseHttpsCallableUserMessage(err);
      setPaymentStepState(prev => ({ ...prev, status: 'idle', error: userMessage }));
    }
  };

  // Show loading while checking auth or provider approval
  if (authLoading || validationState === ValidationState.LOADING) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
        }}
      >
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Validating check-in code...
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Provider (ChartMind clinician roles) checking approval or denied
  if (validationState === ValidationState.VALID && isClinicChartmindProviderRole) {
    if (providerApprovalState === 'loading') {
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
          <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">Checking access...</Typography>
          </Paper>
        </Box>
      );
    }
    if (providerApprovalState === 'error') {
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
          <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 440, textAlign: 'center' }}>
            <ErrorIcon color="warning" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>Could not verify access</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {providerApprovalErrorMessage}
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setProviderApprovalErrorMessage(null);
                setProviderApprovalRetryToken((n) => n + 1);
              }}
            >
              Try again
            </Button>
            <Button sx={{ ml: 1 }} variant="outlined" onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </Paper>
        </Box>
      );
    }
    if (providerApprovalState === 'denied') {
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
          <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 400, textAlign: 'center' }}>
            <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>Not authorized</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You do not have access to use ChartMind for this clinic. Request access from the clinic or facility administrator.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </Paper>
        </Box>
      );
    }
  }

  // Show error states
  if (validationState === ValidationState.INVALID || validationState === ValidationState.ERROR) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          p: 2,
        }}
      >
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 400, textAlign: 'center' }}>
          <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Check-in Unavailable
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </Paper>
      </Box>
    );
  }

  // Show clinic closed
  if (validationState === ValidationState.CLOSED) {
    const isSessionFull = clinicHoursStatus?.sessionFull;
    const hasNextSession = clinicHoursStatus?.nextSessionAt;
    
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          p: 2,
        }}
      >
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 500, textAlign: 'center' }}>
          <EventBusy color={isSessionFull ? 'error' : 'warning'} sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {isSessionFull 
              ? `${clinicInfo?.name || 'Clinic'} - Session Full`
              : `${clinicInfo?.name || 'Clinic'} is Currently Closed`
            }
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {clinicHoursStatus?.reason || 'The clinic is not currently accepting check-ins.'}
          </Typography>
          
          {/* Show capacity info if session is full */}
          {isSessionFull && clinicHoursStatus?.capacity && (
            <Chip 
              label={`${clinicHoursStatus.currentCount}/${clinicHoursStatus.capacity} patients checked in`}
              color="error"
              variant="outlined"
              sx={{ mb: 3 }}
            />
          )}
          
          {/* Show next session time if morning is full but afternoon available */}
          {hasNextSession && (
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Afternoon session opens at:</strong> {clinicHoursStatus.nextSessionAt}
                <br />
                Please return after this time to check in.
              </Typography>
            </Alert>
          )}
          
          {clinicHoursStatus?.nextOpen && !hasNextSession && (
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Next open:</strong> {clinicHoursStatus.nextOpen.toLocaleString([], { 
                  weekday: 'long', 
                  month: 'short', 
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </Typography>
            </Alert>
          )}
          
          {/* Display operating hours */}
          {operatingHours?.weeklyHours && (
            <Paper elevation={0} sx={{ ...contentCardSx, p: 2, mb: 3, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Schedule fontSize="small" color="primary" />
                <Typography variant="subtitle2">Operating Hours</Typography>
              </Box>
              {Object.entries(operatingHours.weeklyHours).map(([day, hours]) => (
                <Box 
                  key={day} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    py: 0.5,
                    borderBottom: day !== 'sunday' ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                    {day}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={hours === 'Closed' ? 'text.disabled' : 'text.primary'}
                  >
                    {hours}
                  </Typography>
                </Box>
              ))}
              {operatingHours.byAppointmentOnly && (
                <Chip 
                  label="By Appointment Only" 
                  size="small" 
                  color="secondary" 
                  sx={{ mt: 1.5 }} 
                />
              )}
            </Paper>
          )}
          
          {operatingHours?.is24x7 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This clinic is normally open 24/7. Please contact the clinic directly for assistance.
            </Alert>
          )}
          
          <Button variant="outlined" onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </Paper>
      </Box>
    );
  }

  // Show check-in complete
  if (checkInComplete) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          p: 2,
        }}
      >
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 500, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Check-in Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You have successfully checked in at {clinicInfo?.name || 'the clinic'}. 
            The staff has been notified of your arrival.
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>What's next?</strong><br />
              Please have a seat in the waiting area. A staff member will call you when it's your turn.
            </Typography>
          </Alert>
          <Chip 
            label={`Check-in ID: ${visitId?.slice(-8) || 'N/A'}`} 
            variant="outlined" 
            sx={{ mb: 3 }}
          />
          <Box>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>
              Go to My Dashboard
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.100',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          {/* Clinic Header */}
          <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3, textAlign: 'center' }}>
            <LocalHospital color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" gutterBottom>
              {clinicInfo?.name || 'Healthcare Clinic'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Patient Check-in
            </Typography>
          </Paper>

          {/* Login/Register Options */}
          <Paper elevation={0} sx={{ ...contentCardSx, p: 4 }}>
            <Typography variant="h6" gutterBottom textAlign="center">
              Sign in to Check In
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
              To check in, please sign in to your HealthDesk account or create a new one.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Login />}
                onClick={handleLogin}
                fullWidth
              >
                Sign In
              </Button>
              
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  or
                </Typography>
              </Divider>
              
              <Button
                variant="outlined"
                size="large"
                startIcon={<PersonAdd />}
                onClick={handleCreateAccount}
                fullWidth
              >
                Create New Account
              </Button>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                Creating an account allows you to securely store your health information 
                and speeds up future check-ins.
              </Typography>
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Check if user is a patient, p2, or p4 (same access for check-in)
  if (userData?.role && userData.role !== 'patient' && userData.role !== 'p2' && userData.role !== 'p4') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          p: 2,
        }}
      >
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 400, textAlign: 'center' }}>
          <ErrorIcon color="warning" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Patient Account Required
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            This check-in link is for patients only. You are currently logged in as a 
            <strong> {userData.role}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please log out and sign in with a patient account, or create a new patient account.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleCreateAccount}
            >
              Create Patient Account
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/auth')}
            >
              Sign In as Different User
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Show the check-in form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.100',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        {/* Clinic Header */}
        <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3, textAlign: 'center' }}>
          <LocalHospital color="primary" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h5" gutterBottom>
            {clinicInfo?.name || 'Healthcare Clinic'}
          </Typography>
          <Chip label="Patient Check-in" color="primary" />
        </Paper>

        {/* Check-in: ID Capture -> Form -> Conversation -> Submit */}
        {checkInStep === 'id-capture' && (
          <PatientIDCaptureStep
            requirePhotoID={template?.requirePhotoID || false}
            requireInsuranceCard={template?.requireInsuranceCard || false}
            clinicId={clinicId}
            templateId={templateId}
            onComplete={handleIDCaptureComplete}
          />
        )}
        {checkInStep === 'form' && (
          <PatientCheckInForm
            template={template}
            userData={userData}
            onSubmit={handleCheckInSubmit}
            akaNames={cmrHistory.akaNames}
            previousDOBs={cmrHistory.previousDOBs}
            idScanData={idCaptureData?.idScan || null}
          />
        )}
        {checkInStep === 'conversation' && resolvedConvoTemplate && (
          <Box sx={{ width: '100%' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Check-in assistant:</strong> Information you provide is summarized for clinic staff. Call your
              local emergency number if you have an emergency. The clinic—not this screen—decides your care.
            </Alert>
            <CheckInConversation
              conversationTemplate={resolvedConvoTemplate}
              patientCMR={pendingFormData?.patientData}
              medicationsReview={template?.medicationsReview || 'none'}
              endActions={resolvedConvoTemplate?.endActions ?? []}
              suggestedQuestions={resolvedConvoTemplate?.suggestedQuestions ?? []}
              onComplete={handleConversationComplete}
              onSkip={handleConversationSkip}
            />
          </Box>
        )}
        {checkInStep === 'medications-review' && (
          <CheckInMedicationsReview
            mode={template?.medicationsReview || 'medications'}
            onComplete={handleMedicationsReviewComplete}
            onSkip={handleMedicationsReviewSkip}
          />
        )}
        {checkInStep === 'review-and-correct' && pendingFormData?.conversationResults && (
          <CheckInReviewAndCorrect
            conversationResults={pendingFormData.conversationResults}
            onConfirm={handleReviewAndCorrectConfirm}
          />
        )}
        {checkInStep === 'face-enrollment' && (
          <CheckInFaceStep
            mode="enrollment"
            onSuccess={handleFaceEnrollmentSuccess}
          />
        )}
        {checkInStep === 'face-verification' && (
          <CheckInFaceStep
            mode="verification"
            onSuccess={handleFaceVerificationSuccess}
          />
        )}
        {checkInStep === 'payment' && registrationFee && (
          <Paper elevation={0} sx={{ ...contentCardSx, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {registrationFee.label || 'Registration fee'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              KSh {registrationFee.amountKsh}
            </Typography>
            {(registrationFee.provider || 'paystack') === 'paystack' && (
              <>
                <TextField
                  fullWidth
                  label="M-Pesa phone number"
                  placeholder="254XXXXXXXXX"
                  value={paymentStepState.phone}
                  onChange={(e) => setPaymentStepState(prev => ({ ...prev, phone: e.target.value, error: null }))}
                  sx={{ mb: 2 }}
                  disabled={paymentStepState.status === 'pending' || paymentStepState.status === 'initiating'}
                />
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handlePayRegistrationFee}
                  disabled={paymentStepState.status === 'pending' || paymentStepState.status === 'initiating'}
                  startIcon={(paymentStepState.status === 'pending' || paymentStepState.status === 'initiating') ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {paymentStepState.status === 'initiating' ? 'Starting…' : paymentStepState.status === 'pending' ? 'Waiting for payment…' : 'Pay with M-Pesa (Paystack)'}
                </Button>
              </>
            )}
            {(registrationFee.provider || 'paystack') === 'stripe' && (
              <Button
                variant="contained"
                fullWidth
                onClick={handlePayRegistrationFee}
                disabled={paymentStepState.status === 'pending' || paymentStepState.status === 'initiating'}
                startIcon={(paymentStepState.status === 'pending' || paymentStepState.status === 'initiating') ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {paymentStepState.status === 'initiating' ? 'Starting…' : 'Pay with card (Stripe)'}
              </Button>
            )}
            {paymentStepState.error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setPaymentStepState(prev => ({ ...prev, error: null }))}>
                {paymentStepState.error}
              </Alert>
            )}
          </Paper>
        )}
        {checkInStep === 'submitting' && (
          <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Submitting your check-in...
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default PatientCheckInPage;
